import React, { useState, useEffect } from 'react';
import { Settings, Bell, Lock, Database, Sliders, Users, Mail, Globe, Zap, Save, Upload, Trash2, FileText } from 'lucide-react';
import api from '../services/api';

const SettingsView = ({ onSuccess }) => {
  const [settings, setSettings] = useState({
    // Notifications
    emailNotifications: true,
    slackNotifications: false,
    lowConfidenceAlerts: true,
    overrideAlerts: true,
    dailyReports: true,
    
    // AI Settings
    confidenceThreshold: 75,
    autoRouting: true,
    learningMode: true,
    
    // Integration Settings
    crmSystem: 'VinSolutions',
    emailProvider: 'Office 365',
    
    // Organization
    companyName: 'Mile High Motors',
    timezone: 'America/Denver',
    currency: 'USD'
  });

  const [forms, setForms] = useState([]);
  const [uploadingForm, setUploadingForm] = useState(false);

  // Load forms when component mounts
  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      const formsList = await api.settings.forms.list();
      setForms(formsList);
    } catch (error) {
      console.error('Failed to load forms:', error);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    // In production, this would call your API
    onSuccess('Settings saved successfully');
  };

  const handleFormUpload = async () => {
  const nameInput = document.getElementById('formName');
  const typeSelect = document.getElementById('formType');
  
  if (!nameInput.value.trim()) {
    onSuccess('Please enter a form name', 'error');
    return;
  }
  
  setUploadingForm(true);
  
  try {
    // Call the API correctly - backend expects name and form_type as query params
    const response = await fetch(`http://localhost:8000/api/v1/settings/forms?name=${encodeURIComponent(nameInput.value)}&form_type=${typeSelect.value}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    
    await loadForms();
    onSuccess('Form uploaded successfully');
    nameInput.value = '';
  } catch (error) {
    console.error('Form upload error:', error);
    onSuccess('Failed to upload form', 'error');
  } finally {
    setUploadingForm(false);
  }
};

  const handleFormDelete = async (formId) => {
    try {
      await api.settings.forms.delete(formId);
      await loadForms();
      onSuccess('Form deleted successfully');
    } catch (error) {
      onSuccess('Failed to delete form', 'error');
    }
  };

  return (
    <>
      <div className="header">
        <h1>Settings</h1>
        <p>Configure system preferences and integrations</p>
      </div>

      {/* Settings Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Notifications Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Bell size={24} color="#00D4AA" />
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
              Notifications
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                Email Notifications
              </span>
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                Slack Notifications
              </span>
              <input
                type="checkbox"
                checked={settings.slackNotifications}
                onChange={(e) => handleSettingChange('slackNotifications', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                Low Confidence Alerts
              </span>
              <input
                type="checkbox"
                checked={settings.lowConfidenceAlerts}
                onChange={(e) => handleSettingChange('lowConfidenceAlerts', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                Manager Override Alerts
              </span>
              <input
                type="checkbox"
                checked={settings.overrideAlerts}
                onChange={(e) => handleSettingChange('overrideAlerts', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                Daily Performance Reports
              </span>
              <input
                type="checkbox"
                checked={settings.dailyReports}
                onChange={(e) => handleSettingChange('dailyReports', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>
          </div>
        </div>

        {/* AI Settings Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Zap size={24} color="#00D4AA" />
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
              AI Configuration
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>
                Confidence Threshold: {settings.confidenceThreshold}%
              </label>
              <input
                type="range"
                min="50"
                max="95"
                value={settings.confidenceThreshold}
                onChange={(e) => handleSettingChange('confidenceThreshold', parseInt(e.target.value))}
                className="confidence-slider"
                style={{
                  width: '100%',
                  height: '8px',
                  background: `linear-gradient(to right, #00D4AA 0%, #00D4AA ${((settings.confidenceThreshold - 50) / 45) * 100}%, rgba(255, 255, 255, 0.1) ${((settings.confidenceThreshold - 50) / 45) * 100}%, rgba(255, 255, 255, 0.1) 100%)`,
                  borderRadius: '4px',
                  outline: 'none',
                  cursor: 'pointer',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '8px' }}>
                Leads below this confidence score will be flagged for review
              </p>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Auto-Routing Enabled
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                  AI automatically routes leads to team members
                </div>
              </div>
              <input
                type="checkbox"
                checked={settings.autoRouting}
                onChange={(e) => handleSettingChange('autoRouting', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Learning Mode
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                  AI learns from manager overrides to improve routing
                </div>
              </div>
              <input
                type="checkbox"
                checked={settings.learningMode}
                onChange={(e) => handleSettingChange('learningMode', e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </label>
          </div>
        </div>

        {/* Integrations Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Database size={24} color="#00D4AA" />
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
              Integrations
            </h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                CRM System
              </label>
              <select
                value={settings.crmSystem}
                onChange={(e) => handleSettingChange('crmSystem', e.target.value)}
                style={{
                  width: '100%',
                  background: '#1a2332',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              >
                <option value="VinSolutions">VinSolutions</option>
                <option value="DriveCentric">DriveCentric</option>
                <option value="DealerSocket">DealerSocket</option>
                <option value="Elead">Elead CRM</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                Email Provider
              </label>
              <select
                value={settings.emailProvider}
                onChange={(e) => handleSettingChange('emailProvider', e.target.value)}
                style={{
                  width: '100%',
                  background: '#1a2332',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              >
                <option value="Office 365">Office 365</option>
                <option value="Gmail">Gmail</option>
                <option value="Outlook">Outlook</option>
              </select>
            </div>
          </div>
        </div>

        {/* Organization Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Globe size={24} color="#00D4AA" />
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
              Organization
            </h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                Company Name
              </label>
              <input
                type="text"
                value={settings.companyName}
                onChange={(e) => handleSettingChange('companyName', e.target.value)}
                style={{
                  width: '100%',
                  background: '#1a2332',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                Timezone
              </label>
              <select
                value={settings.timezone}
                onChange={(e) => handleSettingChange('timezone', e.target.value)}
                style={{
                  width: '100%',
                  background: '#1a2332',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              >
                <option value="America/Denver">Mountain Time (Denver)</option>
                <option value="America/New_York">Eastern Time (New York)</option>
                <option value="America/Chicago">Central Time (Chicago)</option>
                <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) => handleSettingChange('currency', e.target.value)}
                style={{
                  width: '100%',
                  background: '#1a2332',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              >
                <option value="USD">USD ($)</option>
                <option value="CAD">CAD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Forms Repository Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Database size={24} color="#00D4AA" />
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
              Forms Repository
            </h3>
          </div>

          {/* Add Form Input Area */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                  Form Name
                </label>
                <input
                  id="formName"
                  type="text"
                  placeholder="e.g., Customer Trade-In Form"
                  style={{
                    width: '100%',
                    background: '#1a2332',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                  Form Type
                </label>
                <select
                  id="formType"
                  style={{
                    width: '100%',
                    background: '#1a2332',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                >
                  <option value="trade_in">Trade-In</option>
                  <option value="finance">Finance</option>
                  <option value="accessory">Accessory</option>
                  <option value="service">Service</option>
                  <option value="lease">Lease</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>

              <button
                onClick={handleFormUpload}
                disabled={uploadingForm}
                style={{
                  background: uploadingForm ? 'rgba(0, 212, 170, 0.3)' : 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                  border: '2px solid rgba(0, 212, 170, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: uploadingForm ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap'
                }}
              >
                <Upload size={16} />
                {uploadingForm ? 'Uploading...' : 'Add Form'}
              </button>
            </div>
          </div>

          {/* Forms List */}
          {forms.length === 0 ? (
            <div style={{ 
              background: 'rgba(0, 0, 0, 0.3)', 
              border: '1px solid rgba(255, 255, 255, 0.1)', 
              borderRadius: '12px', 
              padding: '40px',
              textAlign: 'center'
            }}>
              <Database size={48} color="rgba(255, 255, 255, 0.3)" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>
                No forms uploaded yet. Add a form using the fields above.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {forms.map(form => (
                <div
                  key={form.id}
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FileText size={20} color="#00D4AA" />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                        {form.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                        Type: {form.type} • Uploaded: {new Date(form.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleFormDelete(form.id)}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#ef4444',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          style={{
            background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
            border: '2px solid rgba(0, 212, 170, 0.3)',
            borderRadius: '12px',
            padding: '16px 32px',
            color: '#FFFFFF',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            alignSelf: 'flex-start'
          }}
        >
          <Save size={20} />
          Save Settings
        </button>
      </div>
    </>
  );
};

export default SettingsView;